import React, { useEffect, useRef, useState } from 'react';
// No ResultTable here: every table on a canvas card now goes through
// PaginatedTable, which wraps it. Chat still uses ResultTable directly.
import { ChartBlock, PieBlock, CalloutsBlock } from '../shared/blocks/ResultBlocks';
import PaginatedTable from './PaginatedTable';
import PivotBlockBody from './PivotBlockBody';
import PivotArranger from './PivotArranger';
import { manualTableRows } from './canvasExport';
import BlockSkeleton from './BlockSkeleton';
import ManualTableGrid from './ManualTableGrid';
import BlockResultEditor from './BlockResultEditor';
import TextBlockArea from './TextBlockArea';
import Markdown from '../chat/Markdown';
import api from '../../utils/api';
import { ui, btnIcon, btnGhost, microLabel, TYPE_HUES, isRunnableBlock, agentVizView } from './canvasUi';
import { IconAgent, IconChart, IconTable, IconScorecard, IconText, IconImage, IconPivot, IconRefresh, IconGear, IconTrash, IconGrip, IconChat, IconPencil, IconPalette } from './icons';
import { resolveBlockCharts } from './chartResolve';

/**
 * One canvas block card in the Ledger identity: a 2px left spine carries the
 * block's type hue (the surface's signature), the header is the drag handle,
 * and action icons fade in on hover (canvas.css). Rendering reuses the chat's
 * block components — same Chart_data / rows contracts.
 *
 * The header deliberately shows the title ALONE. It used to sit under an
 * uppercase type eyebrow, which read as duplication on every unconfigured
 * block ("TEXT / Text", "AGENT / Ask the agent"). The spine still encodes the
 * type, and the palette that created the block already named it.
 */

const s = {
    // Base border lives in canvas.css (.cvx-card) so :hover can deepen it;
    // only the type-hue spine is inline — it must beat the hover rule.
    card: (hue) => ({
        height: '100%', display: 'flex', flexDirection: 'column',
        background: ui.surface, borderLeft: `2px solid ${hue}`,
        borderRadius: ui.radius, overflow: 'hidden',
    }),
    head: { display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 12px 9px 10px', borderBottom: `1px solid ${ui.hairline}`, cursor: 'move', background: ui.surface },
    grip: { display: 'inline-flex', flexShrink: 0 }, // color comes from .cvx-grip so hover can darken it
    headText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' },
    title: { fontSize: '13px', fontWeight: 650, color: ui.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 },
    actions: { display: 'inline-flex', gap: '2px', flexShrink: 0 },
    btn: { ...btnIcon, width: '26px', height: '26px' },
    body: { flex: 1, overflow: 'auto', padding: '12px 14px', minHeight: 0 },

    // Compact invitation zone — replaces the old full-void empty state.
    zone: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '7px', border: '1.5px dashed #c9cfdb', borderRadius: ui.radiusSm, color: ui.ink2, fontSize: '12.5px', textAlign: 'center', padding: '12px' },
    zoneIcon: (hue) => ({ color: hue, opacity: 0.75, display: 'inline-flex' }),
    ghostSm: { ...btnGhost, padding: '6px 12px', fontSize: '12.5px' },

    // Image block: fills the card, letterboxed (contain) or cropped (cover)
    // per config.fit; alt doubles as a caption shown beneath.
    imageWrap: { height: '100%', display: 'flex', flexDirection: 'column', gap: '6px' },
    imageEl: (fit) => ({ width: '100%', flex: 1, minHeight: 0, borderRadius: ui.radiusSm, objectFit: fit === 'cover' ? 'cover' : 'contain', background: '#f6f7f9', display: 'block' }),
    imageCaption: { fontSize: '11.5px', color: ui.ink2, textAlign: 'center', flexShrink: 0 },
    // Cancel sits inline next to the spinner/status text (either centered
    // under the bare spinner, or in the sticky staleBar row) — muted so it
    // reads as a secondary affordance, not competing with the status label.
    cancelBtn: { ...btnGhost, padding: '5px 12px', fontSize: '12px', color: ui.ink2, flexShrink: 0 },

    center: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '9px', color: ui.ink2, fontSize: '13px', textAlign: 'center', padding: '12px' },
    // Top-aligned, unlike `center`: the skeleton stands in for the real body,
    // so it has to sit where that body will sit or the card still jumps.
    pending: { height: '100%', display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 2px', overflow: 'hidden' },
    manualWrap: { height: '100%', display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'auto', paddingRight: '2px' },
    manualHint: { fontSize: '12px', color: ui.ink2 },
    manualTitle: { border: '1px solid transparent', background: 'transparent', borderRadius: ui.radiusSm, padding: '3px 6px', margin: '-3px -6px 2px', fontSize: '13px', fontWeight: 650, fontFamily: 'inherit', color: ui.ink, outline: 'none', width: '100%', boxSizing: 'border-box' },
    inlineEdit: { height: '100%', display: 'flex', flexDirection: 'column', gap: '8px' },
    inlineArea: { flex: 1, minHeight: '80px', width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: ui.radiusSm, border: ui.border, fontSize: '13px', fontFamily: 'inherit', lineHeight: 1.6, outline: 'none', color: ui.ink, resize: 'none' },
    inlineInput: { flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: ui.radiusSm, border: ui.border, fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', color: ui.ink },
    inlineFoot: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, paddingTop: '6px' },
    inlineEditRows: { display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, paddingTop: '8px' },
    error: { color: ui.danger, fontSize: '12.5px', whiteSpace: 'pre-wrap', maxWidth: '100%' },
    score: { fontSize: '32px', fontWeight: 700, color: ui.ink, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 },
    scoreLabel: { ...microLabel, fontSize: '10.5px' },

    // Chart/Table switch on an agent block that carries both. A hairline
    // segmented control, right-aligned so it reads as a view control rather
    // than competing with the result itself.
    vizToggle: { display: 'flex', justifyContent: 'flex-end', gap: '2px', marginBottom: '8px' },
    vizToggleBtn: {
        appearance: 'none', border: `1px solid ${ui.hairline}`, background: 'transparent',
        color: ui.ink2, fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600,
        padding: '3px 10px', cursor: 'pointer',
    },
    vizToggleOn: { background: '#f0f5ff', borderColor: ui.signal, color: ui.signal },

    // The row above each chart: an issue chip on the left, the chart's own
    // settings button on the right. Rendered only when there is something
    // to put in it, so an untouched chart gains no furniture.
    chartRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', minHeight: '26px' },
    chipError: { display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '11.5px', fontWeight: 600, color: '#8a5a10', background: '#fdf6e7', border: '1px solid #f3dcb0', borderRadius: '99px', padding: '3px 10px', minWidth: 0 },
    chipNote: { display: 'inline-flex', alignItems: 'center', fontSize: '11.5px', fontWeight: 600, color: ui.ink2, background: '#f4f6fa', border: `1px solid ${ui.hairline}`, borderRadius: '99px', padding: '3px 10px', cursor: 'pointer', fontFamily: 'inherit' },
    chipLink: { appearance: 'none', border: 'none', background: 'transparent', color: ui.signal, fontWeight: 700, fontSize: '11.5px', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },

    // A re-run dims the previous result instead of erasing it. The strip is
    // sticky against the scrolling body (negative offsets pull it flush with
    // the body's padding box, so it never appears to move) — the status stays
    // readable however far down the user has scrolled.
    staleBar: {
        position: 'sticky', top: '-12px', zIndex: 2,
        display: 'flex', alignItems: 'center', gap: '8px',
        margin: '-12px -14px 8px', padding: '8px 14px',
        background: 'rgba(255,255,255,0.94)', borderBottom: `1px solid ${ui.hairline}`,
        fontSize: '12px', fontWeight: 600, color: ui.ink2,
    },
    staleBody: { opacity: 0.4, pointerEvents: 'none' },
    spinnerSm: { width: '13px', height: '13px', borderRadius: '50%', border: `2px solid ${ui.hairline}`, borderTopColor: ui.signal, animation: 'spin 0.8s linear infinite', flexShrink: 0 },

    timeline: { display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '12px', color: ui.ink2, width: '100%' },
    timelineCenter: { display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12.5px', color: ui.ink2, alignItems: 'flex-start', maxWidth: '92%' },
    timelineList: { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, maxWidth: '100%' },
    timelineRow: { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
    // One neutral marker for every step — see RunTimeline for why a per-step
    // tick/spinner cannot be honest once tool calls run in parallel.
    timelineDot: { color: ui.ink2, opacity: 0.5, fontSize: '11px', width: '13px', textAlign: 'center', flexShrink: 0 },
    timelineStep: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    timelineEarlier: { opacity: 0.45, fontSize: '11px' },
    timelineFoot: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '2px', fontVariantNumeric: 'tabular-nums', fontSize: '11.5px', opacity: 0.8 },
    staleStrip: {
        position: 'sticky', top: '-12px', zIndex: 2,
        margin: '-12px -14px 8px', padding: '8px 14px',
        background: 'rgba(255,255,255,0.94)', borderBottom: `1px solid ${ui.hairline}`,
    },

    // A failed run is server-owned (block.run) and independent of block.result
    // — a re-run failure preserves the last good result rather than clobbering
    // it. This strip sits above that preserved result so the failure isn't
    // silently lost; see the `!r || r.error` branch below for the case where
    // there's no result to preserve at all.
    failBar: {
        display: 'flex', alignItems: 'center', gap: '8px',
        margin: '-12px -14px 8px', padding: '8px 14px',
        background: '#fdf1f1', borderBottom: `1px solid #f3d3d4`,
        fontSize: '12px', fontWeight: 600, color: ui.danger,
    },
};

// Block types whose rendered RESULT can be hand-edited — mirrors the server's
// EDITABLE_RESULT_TYPES (canvasResultEdit.js). Text and image are editable too,
// but through their config, not their result.
const RESULT_EDITABLE = new Set(['scorecard', 'table', 'chart', 'agent']);

const TYPE_META = {
    agent: { Icon: IconAgent, label: 'Agent' },
    chart: { Icon: IconChart, label: 'Chart' },
    table: { Icon: IconTable, label: 'Table' },
    scorecard: { Icon: IconScorecard, label: 'Scorecard' },
    text: { Icon: IconText, label: 'Text' },
    image: { Icon: IconImage, label: 'Image' },
    manualtable: { Icon: IconTable, label: 'Manual table' },
    pivot: { Icon: IconPivot, label: 'Pivot' },
};

/**
 * The card's heading, or '' when the block has no name of its own.
 *
 * Deliberately NO type-name fallback: "Text" / "Image" / "Table" told the
 * reader nothing the card's own icon and body did not already say, and on a
 * dashboard full of blocks that noise read as a row of labels rather than a
 * set of titles. An unnamed block simply has no heading. The export title
 * (canvasExport.js#canvasBlockTitle) keeps its own fallback — a slide or a
 * spreadsheet tab with no heading at all is worse than a generic one.
 */
function blockTitle(block) {
    if (block.type === 'agent') return block.result?.headline || block.config?.prompt || '';
    if (block.type === 'chart') return block.result?.Chart_data?.chart_header || '';
    if (block.type === 'scorecard') return block.result?.label || block.config?.label || '';
    if (block.type === 'image') return block.config?.title || block.config?.alt || '';
    if (block.type === 'manualtable') return block.config?.title || '';
    if (block.type === 'text') return block.config?.title || '';
    if (block.type === 'pivot') return block.config?.title || '';
    return '';
}

/**
 * An agent block's body: its viz (chart or table) plus prose callouts, with a
 * segmented control to switch between the two — shown ONLY when the result
 * actually carries both. A one-option toggle would be furniture.
 *
 * The choice is written to `config.vizView` rather than component state so it
 * survives a reload and travels with the block: the export and email paths
 * read the same field, so a deck matches what the author last looked at.
 */
/**
 * One chart with its chrome: the issues the resolver raised (an error means
 * "this is the agent's chart, not yours" — amber, with a Fix link) and the
 * chart's own settings button. The chart title stays inside the shared
 * renderer; this row sits above it and only renders when it has something
 * to hold, so a chart nobody touched looks exactly as before.
 */
function ChartFrame({ entry, canEdit, onChartSettings }) {
    const { chart, issues, index } = entry;
    const error = issues.find((i) => i.severity === 'error');
    const notes = issues.filter((i) => i.severity !== 'error');
    const showBtn = canEdit && Boolean(onChartSettings);
    const open = () => onChartSettings(index);
    const noteText = `ⓘ ${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`;
    return (
        <div data-testid="canvas-chart-frame">
            {(error || notes.length > 0 || showBtn) && (
                <div style={s.chartRow}>
                    {error && (
                        <span style={s.chipError} role="status" data-testid="canvas-chart-issue">
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{error.message}</span>
                            {showBtn && <button type="button" className="cvx-focus" style={s.chipLink} onClick={open} onMouseDown={(e) => e.stopPropagation()}>Fix</button>}
                        </span>
                    )}
                    {!error && notes.length > 0 && (showBtn
                        ? <button type="button" className="cvx-focus" style={s.chipNote} onClick={open} onMouseDown={(e) => e.stopPropagation()} title={notes.map((n) => n.message).join('\n')} data-testid="canvas-chart-note">{noteText}</button>
                        : <span style={s.chipNote} title={notes.map((n) => n.message).join('\n')} data-testid="canvas-chart-note">{noteText}</span>)}
                    <span style={{ flex: 1 }} />
                    {showBtn && (
                        <button type="button" className="cvx-iconbtn cvx-focus" style={s.btn} onClick={open} onMouseDown={(e) => e.stopPropagation()}
                            title="Chart settings" aria-label={`Chart ${index + 1} settings`} data-testid="canvas-chart-settings">
                            <IconPalette size={14} />
                        </button>
                    )}
                </div>
            )}
            {chart && (chart.look
                ? <ChartBlock chartData={chart} />
                : chart.chart_type === 'pie' ? <PieBlock chartData={chart} /> : <ChartBlock chartData={chart} />)}
        </div>
    );
}

function AgentResultView({ block, r, onConfigChange, readOnly, onChartSettings }) {
    const viz = r.viz;
    const { hasChart, hasTable, view, entries } = agentVizView(block, viz);
    const canToggle = hasChart && hasTable && !readOnly && Boolean(onConfigChange);
    // A table-only answer can still become a chart: the reader picks columns
    // in Chart settings. Offered in the toggle row, where the chart would be.
    const canChartTable = !hasChart && hasTable && !readOnly && Boolean(onChartSettings);
    // A table-only block whose derivation FAILED still has an entry — chart
    // null, an error issue — but `view` is 'table', so the ordinary chart
    // loop below never renders it and the failure would otherwise vanish.
    const failed = entries.filter((e) => !e.chart && e.issues.length > 0);

    return (
        <div>
            {(canToggle || canChartTable) && (
                <div style={s.vizToggle} role="group" aria-label="Show as">
                    {canChartTable && (
                        <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => onChartSettings(0)} data-testid="canvas-chart-this-table">
                            <IconPalette size={13} /> Chart this table
                        </button>
                    )}
                    {canToggle && [['chart', 'Chart'], ['table', 'Table']].map(([v, label]) => (
                        <button key={v} type="button"
                            className="cvx-focus"
                            style={{ ...s.vizToggleBtn, ...(view === v ? s.vizToggleOn : {}) }}
                            aria-pressed={view === v}
                            data-testid={`canvas-viz-${v}`}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => onConfigChange({ ...(block.config || {}), vizView: v })}>
                            {label}
                        </button>
                    ))}
                </div>
            )}
            {view === 'table' && failed.map((entry) => (
                <ChartFrame key={entry.index} entry={entry} canEdit={!readOnly} onChartSettings={onChartSettings} />
            ))}
            {view === 'table' && <PaginatedTable rows={viz.table} />}
            {view === 'chart' && entries.map((entry) => (
                <ChartFrame key={entry.index} entry={entry} canEdit={!readOnly} onChartSettings={onChartSettings} />
            ))}
            <CalloutsBlock text={r.callouts} />
        </div>
    );
}

/** A successful result, rendered. Split out so a run in flight can keep showing
 *  the previous one underneath its status strip. */
function ResultView({ block, r, onConfigChange, readOnly, onArrangePivot, onChartSettings }) {
    if (block.type === 'scorecard') {
        return (
            <div style={s.center}>
                <div style={s.score}>{Number(r.value).toLocaleString()}</div>
                <div style={s.scoreLabel}>{r.label}</div>
            </div>
        );
    }
    if (block.type === 'table') return <PaginatedTable rows={r.rows} />;
    if (block.type === 'pivot') {
        // Rearranging happens ON THE CARD for anyone who can edit it and has
        // rows to pivot — that is the whole interaction, so burying it in a
        // drawer made the block feel inert.
        //
        // Everyone else renders the SERVER's stored matrix: a viewer has no
        // business rearranging someone's dashboard, and a public link-holder
        // could not anyway — sanitizeCanvasForPublic withholds `result.rows`,
        // so there is no data in the browser to pivot. That fallback is also
        // what keeps the card honest between a rearrangement and the /repivot
        // that persists it.
        const canArrange = !readOnly && onArrangePivot && Array.isArray(r.rows) && r.rows.length > 0;
        return canArrange
            ? (
                <PivotArranger pivot={block.config?.pivot} rows={r.rows} onCard
                    onChange={(pivot) => onArrangePivot(block, pivot)} />
            )
            : <PivotBlockBody block={block} r={r} />;
    }
    if (block.type === 'chart') {
        const [entry] = resolveBlockCharts(block);
        if (!entry?.chart) return <div style={s.center}>No chart data.</div>;
        return <ChartFrame entry={entry} canEdit={!readOnly} onChartSettings={onChartSettings} />;
    }
    return <AgentResultView block={block} r={r} onConfigChange={onConfigChange} readOnly={readOnly} onChartSettings={onChartSettings} />;
}

const TIMELINE_MAX = 5;

function fmtElapsed(sec) {
    return sec >= 60 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${sec}s`;
}

/** The running state: the run's accumulated steps, plus an elapsed ticker so
 *  the card visibly moves even between sparse steps (a sandbox call can be
 *  ~20s of silence). startMs comes from the live run record when this is a
 *  reattach; a fresh run falls back to first-mount time.
 *
 *  The steps are an append-only LOG of things that happened, NOT a checklist
 *  of states — so every line carries the same neutral marker. They used to
 *  tick everything above the last line and spin beside it, which encoded
 *  "the last line is what's happening now, everything above it finished".
 *  That was true while tool calls ran sequentially and became a lie the day
 *  the agent loop started batching them (cap 4): two parallel analyses emit
 *  "Analysis 1: crunching…" / "Analysis 2: crunching…" / "Analysis 2
 *  finished" / "Analysis 1 finished", so position stopped implying
 *  completion — a ✓ landed beside a still-running analysis and a spinner
 *  beside a finished one. The single spinner therefore lives in the footer
 *  next to the elapsed time, where it only ever claims "this run is still
 *  going" — always true while `running`, so it cannot lie.
 *
 *  aria-live wraps ONLY the step list. The elapsed footer re-renders every
 *  second; inside the live region that made a screen reader announce the
 *  whole timeline once per second for the entire run. Outside it, the
 *  elapsed time is still readable on demand, just not on a timer. */
function RunTimeline({ steps, defaultLabel, startMs, centered, onCancel }) {
    const startRef = useRef(startMs || Date.now());
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const elapsed = Math.max(0, Math.floor((now - startRef.current) / 1000));

    const all = steps.length ? steps : [defaultLabel];
    const shown = all.slice(-TIMELINE_MAX);
    const hidden = all.length - shown.length;

    return (
        <div style={centered ? s.timelineCenter : s.timeline} data-testid="canvas-run-timeline">
            <div style={s.timelineList} aria-live="polite" data-testid="canvas-run-steps">
                {hidden > 0 && <div style={s.timelineEarlier}>+{hidden} earlier</div>}
                {shown.map((text, i) => (
                    <div key={all.length - shown.length + i} style={s.timelineRow}>
                        <span style={s.timelineDot} aria-hidden="true">•</span>
                        <span style={s.timelineStep}>{text}</span>
                    </div>
                ))}
            </div>
            <div style={s.timelineFoot} data-testid="canvas-run-elapsed">
                <span className="cvx-spinner" style={s.spinnerSm} data-testid="canvas-run-spinner" aria-hidden="true" />
                <span>{fmtElapsed(elapsed)}</span>
                {onCancel && (
                    <button type="button" className="cvx-ghost cvx-focus" style={s.cancelBtn} data-testid="canvas-cancel-run" onClick={onCancel}>Cancel</button>
                )}
            </div>
        </div>
    );
}

// Backoff for the retries below. Two attempts covers the save round trip and
// a blip; beyond that the image is genuinely not there and the card should
// say so rather than spin.
const IMAGE_RETRY_DELAYS_MS = [400, 1200];

/** Distinct URL per retry, so neither the browser cache nor a cached 404 can
 *  serve the previous failure back. Separator-aware: the owner URL already
 *  carries ?v=, the share-token one carries nothing. */
function withRetryToken(src, attempt) {
    if (!attempt) return src;
    return `${src}${src.includes('?') ? '&' : '?'}r=${attempt}`;
}

/**
 * The image block body: attempts the stream URL built from ids (imageSrc,
 * computed by CanvasBlockCard below), falling back to the invitation zone
 * whenever the load fails. A shared/public canvas never receives storagePath
 * (sanitizeCanvasForPublic withholds it), so a failed <img> load is the ONLY
 * signal that block has no image — the same fallback also catches a genuine
 * server-side miss (deleted object, wrong tenant, GCP disabled) for the
 * owner/allowlist paths, where CanvasBlockCard already skips rendering an
 * <img> at all when it knows for certain (config.storagePath present and
 * falsy) there is nothing to fetch.
 */
function ImageBlockBody({ src, alt, fit, hue, Icon, readOnly, onConfigure }) {
    const [broken, setBroken] = useState(false);
    // Images stream through our own API, so a large one takes a visible moment
    // — long enough that an empty card reads as "nothing here". Reset on every
    // src change, or replacing a picture would show the new one instantly and
    // then never re-arm for the next.
    const [loaded, setLoaded] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const retryTimer = useRef(null);
    useEffect(() => {
        setBroken(false);
        setLoaded(false);
        setAttempt(0);
        return () => clearTimeout(retryTimer.current);
    }, [src]);

    // A first miss must not be final. The card renders the moment an upload
    // returns, while the PUT that teaches the SERVER this block's storagePath
    // is still in flight — and the stream endpoint reads that from the saved
    // doc, so for a beat it legitimately has nothing to serve. Before this,
    // that one 404 latched and only a page reload cleared it. Also absorbs a
    // transient GCS blip. Give up after the budget so a genuinely absent
    // image still falls back instead of retrying forever.
    const onImgError = () => {
        if (attempt >= IMAGE_RETRY_DELAYS_MS.length) { setBroken(true); return; }
        retryTimer.current = setTimeout(() => setAttempt((n) => n + 1), IMAGE_RETRY_DELAYS_MS[attempt]);
    };

    if (!src || broken) {
        return (
            <div style={s.zone}>
                <span style={s.zoneIcon(hue)}><Icon size={22} /></span>
                <span>{readOnly ? 'No image yet.' : 'Add a picture, screenshot or logo to this dashboard.'}</span>
                {!readOnly && <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onConfigure}>Add image</button>}
            </div>
        );
    }
    return (
        <div style={s.imageWrap} aria-busy={!loaded}>
            {!loaded && <BlockSkeleton type="image" />}
            {/* Kept mounted while loading — unmounting it would cancel the
                request and the load event would never fire. */}
            <img src={withRetryToken(src, attempt)} alt={alt || ''} onError={onImgError} onLoad={() => setLoaded(true)}
                style={loaded ? s.imageEl(fit) : { display: 'none' }} />
            {alt && loaded && <div style={s.imageCaption}>{alt}</div>}
        </div>
    );
}

/**
 * An image's description. `?? alt` rather than `|| alt`: `alt` was the single
 * field doing both title and caption before they split, so it stays readable
 * as a fallback — but a description the user deliberately cleared to "" must
 * stay cleared instead of resurrecting the old text.
 */
const imageCaption = (cfg) => cfg?.caption ?? cfg?.alt ?? '';

function Body({ block, running, statusSteps, readOnly, hue, Icon, onRefresh, onConfigure, onCancelRun, imageSrc, onConfigChange, onArrangePivot, onChartSettings, editing, savingEdit, onStartEdit, onCancelEdit, onCommitResult }) {
    if (block.type === 'text') {
        if (editing) {
            return (
                <div style={s.inlineEdit}>
                    <input className="cvx-focus" style={s.inlineInput} autoFocus value={block.config?.title || ''}
                        placeholder="Title" aria-label="Text block title" maxLength={200}
                        data-testid="canvas-edit-title"
                        onChange={(e) => onConfigChange({ ...(block.config || {}), title: e.target.value })} />
                    <TextBlockArea value={block.config?.markdown || ''} style={s.inlineArea}
                        placeholder={'- First point\n- Second point'}
                        testId="canvas-edit-markdown"
                        onChange={(markdown) => onConfigChange({ ...(block.config || {}), markdown })} />
                    <div style={s.inlineFoot}>
                        <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onCancelEdit}>Done</button>
                    </div>
                </div>
            );
        }
        return block.config?.markdown
            ? <Markdown text={block.config.markdown} />
            : (
                <div style={s.zone}>
                    <span style={s.zoneIcon(hue)}><Icon size={22} /></span>
                    <span>{readOnly ? 'Empty text block.' : 'Write a note, heading or narrative for this dashboard.'}</span>
                    {!readOnly && <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onConfigure}>Write text</button>}
                </div>
            );
    }
    if (block.type === 'image') {
        const cfgImg = block.config || {};
        return (
            <>
                <ImageBlockBody src={imageSrc} alt={imageCaption(cfgImg)} fit={cfgImg.fit}
                    hue={hue} Icon={Icon} readOnly={readOnly} onConfigure={onConfigure} />
                {editing && (
                    <div style={s.inlineEditRows}>
                        {/* Title and description are separate: the title names
                            the block (card heading, export section, slide) and
                            the description sits under the picture. Replacing
                            the picture itself still belongs with the uploader. */}
                        <input className="cvx-focus" style={s.inlineInput} autoFocus value={cfgImg.title || ''}
                            placeholder="Title" aria-label="Image title" maxLength={200}
                            data-testid="canvas-edit-title"
                            onChange={(e) => onConfigChange({ ...cfgImg, title: e.target.value })} />
                        <input className="cvx-focus" style={s.inlineInput} value={imageCaption(cfgImg)}
                            placeholder="Description" aria-label="Image description" maxLength={2000}
                            data-testid="canvas-edit-caption"
                            onChange={(e) => onConfigChange({ ...cfgImg, caption: e.target.value })} />
                        <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onCancelEdit}>Done</button>
                    </div>
                )}
            </>
        );
    }
    if (block.type === 'manualtable') {
        // Config-only, like text and image: there is no result and nothing to
        // run, so it must return before the run-state gates below.
        //
        // Editable in place for anyone who can edit the canvas — a hand-made
        // table is edited far more often than it is created, and bouncing
        // through the drawer for one typo is the wrong loop. The drawer still
        // owns bulk paste and column removal.
        if (!readOnly && onConfigChange) {
            const empty = (block.config?.columns || []).length === 0;
            return (
                <div style={s.manualWrap}>
                    {/* The card IS this block's editor, so its header lives here
                        rather than only in the drawer. Styled as a heading until
                        focused so the card still reads as a dashboard tile. */}
                    <input className="cvx-focus" style={s.manualTitle} value={block.config?.title || ''}
                        placeholder="Table title" aria-label="Table title" maxLength={120}
                        data-testid="canvas-mt-title"
                        onChange={(e) => onConfigChange({ ...(block.config || {}), title: e.target.value })} />
                    {empty && <div style={s.manualHint}>Add a column to start, or use the gear to paste from a spreadsheet.</div>}
                    <ManualTableGrid cfg={block.config || {}} compact
                        onChange={(next) => onConfigChange({ ...(block.config || {}), ...next })} />
                </div>
            );
        }
        const rows = manualTableRows(block.config);
        return rows.length > 0 ? <PaginatedTable rows={rows} /> : (
            <div style={s.zone}>
                <span style={s.zoneIcon(hue)}><Icon size={22} /></span>
                <span>This table is empty.</span>
            </div>
        );
    }
    // The editor stands in for the whole body: showing both would mean two
    // copies of the same figures on one small card.
    if (editing && RESULT_EDITABLE.has(block.type) && block.result) {
        return <BlockResultEditor block={block} saving={savingEdit} onSave={onCommitResult} onCancel={onCancelEdit} onChartSettings={onChartSettings ? () => onChartSettings(0) : undefined} />;
    }
    const r = block.result;
    if (running) {
        const defaultLabel = block.type === 'agent' ? 'Asking the agent…' : 'Fetching data…';
        // block.run is only the LIVE record on a reattach (status running);
        // after a fresh click it still holds the PREVIOUS run until adopt,
        // so its startedAt must be ignored then.
        const startMs = block.run?.status === 'running' && block.run.startedAt
            ? new Date(block.run.startedAt).getTime() : null;
        const timeline = (
            <RunTimeline steps={statusSteps} defaultLabel={defaultLabel} startMs={startMs}
                centered={!r || !!r.error}
                onCancel={onCancelRun ? () => onCancelRun(block) : null} />
        );
        // Nothing good to keep on screen — show the shape the result will take
        // instead of a bare centred spinner, so the card doesn't jump when the
        // data lands. The timeline stays above it: on an agent run it is real
        // streamed progress, not decoration.
        if (!r || r.error) {
            return (
                <div style={s.pending} aria-busy="true">
                    {timeline}
                    <BlockSkeleton type={block.type} />
                </div>
            );
        }
        return (
            <div aria-busy="true">
                <div style={s.staleStrip}>{timeline}</div>
                <div style={s.staleBody}><ResultView block={block} r={r} readOnly onChartSettings={onChartSettings} /></div>
            </div>
        );
    }
    // Cancellation is user intent, not failure — it must never be mistaken
    // for the error branch below. Must run BEFORE the `!r` empty-zone check
    // for the same reason the error branch does: a block cancelled on its
    // very first run (result still null) would otherwise fall into the
    // "Pick a data source…" invitation zone instead of a neutral notice.
    // A cancelled run WITH a previous result intentionally has NO branch
    // here — it falls through to the ordinary ResultView render below, same
    // as any other idle block with a good result, because cancellation must
    // not hide good data.
    if (block.run?.status === 'cancelled' && !r) {
        return <div style={s.center}>Run cancelled</div>;
    }
    // A failed run is recorded on block.run (server-owned), separate from
    // block.result (preserved from the last success — see Task 7). This must
    // run BEFORE the `!r` empty-zone check below: a block that has never
    // succeeded has result === null, and without this ordering its very
    // first failure would fall into the "Pick data sources…" invitation zone
    // instead of showing the error — silently hiding the failure on exactly
    // the reload path this whole plan exists to fix.
    if (block.run?.status === 'error' && block.run.error) {
        if (!r || r.error) {
            // No previous good result to preserve — the error is the whole body.
            return (
                <div style={s.center}>
                    <div style={s.error}>{block.run.error}</div>
                    {!readOnly && <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onRefresh}>Retry</button>}
                </div>
            );
        }
        // A good result survives the failure — show it with a failure strip above.
        return (
            <div>
                <div style={s.failBar} role="status">
                    <span>Last run failed: {block.run.error}</span>
                </div>
                <ResultView block={block} r={r} onConfigChange={onConfigChange} readOnly={readOnly} onArrangePivot={onArrangePivot} onChartSettings={onChartSettings} />
            </div>
        );
    }
    if (!r) {
        return (
            <div style={s.zone}>
                <span style={s.zoneIcon(hue)}><Icon size={22} /></span>
                <span>{readOnly ? 'This block has not run yet.' : block.type === 'agent' ? 'Pick data sources, write a prompt, then run.' : 'Pick a data source, then run.'}</span>
                {!readOnly && <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onConfigure}>Configure</button>}
            </div>
        );
    }
    if (r.error) {
        return (
            <div style={s.center}>
                <div style={s.error}>{r.error}</div>
                {!readOnly && <button type="button" className="cvx-ghost cvx-focus" style={s.ghostSm} onClick={onRefresh}>Retry</button>}
            </div>
        );
    }
    return <ResultView block={block} r={r} onConfigChange={onConfigChange} readOnly={readOnly} onArrangePivot={onArrangePivot} onChartSettings={onChartSettings} />;
}

/** Tiny non-cryptographic hash — this is a cache key, never a secret. */
function shortHash(s) {
    let h = 0;
    for (let i = 0; i < String(s).length; i++) h = (Math.imul(31, h) + String(s).charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
}

/**
 * The stream URL an image block's <img> should point at, built from ids the
 * card already has — same pattern CanvasEditorPage uses for its SSE URL
 * (`${api.defaults.baseURL}/...`), so local CRA-proxy and deployed
 * same-origin both work. `shareToken` (from CanvasSharePage) wins when both
 * are supplied. Gated on `storagePath` ONLY when that field is present in
 * config — an owner or allowlist doc always carries it (even as ""), so a
 * genuinely empty block never issues a doomed request; a public-shared doc
 * never carries it at all (sanitizeCanvasForPublic withholds it), so there
 * the card has to attempt the fetch and let ImageBlockBody's onError decide.
 */
function imageSrcFor(block, canvasId, shareToken) {
    if (block.type !== 'image') return null;
    const cfg = block.config || {};
    const base = api.defaults.baseURL;
    // Public-shared docs never carry storagePath, so there the fetch has to be
    // attempted and ImageBlockBody's onError is the only "no image" signal.
    if (shareToken) return `${base}/canvases/shared/token/${encodeURIComponent(shareToken)}/blocks/${block.id}/image`;
    // Owner/allowlist docs DO carry it, so a block without one is known-empty
    // and must not issue a request that can only 404.
    if (!cfg.storagePath || !canvasId) return null;
    // The version token is load-bearing, not cosmetic. This URL is built from
    // ids alone, so it was byte-identical before and after an upload: the
    // <img> that 404'd while the block was still empty never refetched once
    // storagePath arrived (src never changed, so ImageBlockBody's reset effect
    // never fired), and REPLACING an image kept serving the cached previous
    // one, because the endpoint sends Cache-Control: max-age=300 against that
    // same unchanged URL. Deriving the token from storagePath makes the URL
    // change exactly when the underlying object does. Hashed, not raw, so the
    // object key never travels in a URL, a referrer header or an access log.
    return `${base}/canvases/${canvasId}/blocks/${block.id}/image?v=${shortHash(cfg.storagePath)}`;
}

export default function CanvasBlockCard({ block, canvasId, shareToken, running, statusSteps = [], readOnly = false, onConfigure, onChat, onRefresh, onDelete, onCancelRun, onConfigChange, onArrangePivot, onChartSettings, onEditingChange, onSaveResult }) {
    const meta = TYPE_META[block.type] || TYPE_META.text;
    const hue = TYPE_HUES[block.type] || TYPE_HUES.text;
    const runnable = isRunnableBlock(block.type);
    const [editing, setEditing] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    // Editable content is either a run result (scorecard/table/chart/agent,
    // once there IS one to edit) or plain config (text markdown, image
    // caption). A manual table is already editable in place and needs no mode.
    const canEdit = !readOnly && !running && (
        block.type === 'text' || block.type === 'image'
            ? Boolean(onConfigChange)
            : Boolean(onSaveResult) && RESULT_EDITABLE.has(block.type) && Boolean(block.result) && !block.result.error
    );
    // A run replaces what was typed, so never leave the editor open across one
    // — it would be showing a draft of content that no longer exists.
    useEffect(() => { if (running || !canEdit) setEditing(false); }, [running, canEdit]);

    // The page inflates this block's grid cell while its editor is open, so it
    // has to hear about EVERY transition — including the involuntary ones
    // above and unmounting mid-edit (deleted, or the canvas navigated away).
    // A missed "closed" would leave the grid frozen: expandedId gates layout
    // autosave, so the dashboard would quietly stop remembering any drag.
    const notifyEditing = useRef(onEditingChange);
    notifyEditing.current = onEditingChange;
    useEffect(() => {
        notifyEditing.current?.(block.id, editing);
    }, [editing, block.id]);
    useEffect(() => () => notifyEditing.current?.(block.id, false), [block.id]);

    const commitResult = async (result) => {
        setSavingEdit(true);
        try {
            const ok = await onSaveResult(result);
            if (ok) setEditing(false);
        } finally {
            setSavingEdit(false);
        }
    };

    const imageSrc = imageSrcFor(block, canvasId, shareToken);
    return (
        <div className="cvx-card" style={s.card(hue)} data-testid={`canvas-block-${block.type}`}>
            <div className="cv-drag" style={{ ...s.head, ...(readOnly ? { cursor: 'default' } : {}) }} title={readOnly ? undefined : 'Drag to move'}>
                {!readOnly && <span className="cvx-grip" style={s.grip} aria-hidden="true"><IconGrip size={14} /></span>}
                <span style={s.headText}>
                    <span style={s.title}>{blockTitle(block)}</span>
                </span>
                {!readOnly && (
                <span className={`cvx-actions${running ? ' cvx-actions--pinned' : ''}`} style={s.actions}>
                    {block.type === 'agent' && onChat && (block.thread || []).length > 0 && (
                        <button type="button" className="cvx-iconbtn cvx-focus" style={s.btn} onClick={onChat} disabled={running}
                            title="Refine with a message" aria-label="Refine Agent block with a message" onMouseDown={(e) => e.stopPropagation()}>
                            <IconChat size={14} />
                        </button>
                    )}
                    {runnable && (
                        <button type="button" className="cvx-iconbtn cvx-focus" style={s.btn} onClick={onRefresh} disabled={running}
                            title="Refresh" aria-label={`Refresh ${meta.label} block`} onMouseDown={(e) => e.stopPropagation()}>
                            <IconRefresh size={14} />
                        </button>
                    )}
                    {canEdit && (
                        <button type="button" className="cvx-iconbtn cvx-focus" style={s.btn} onClick={() => setEditing((v) => !v)}
                            disabled={running} title={editing ? 'Stop editing' : 'Edit content'}
                            aria-label={`Edit ${meta.label} block content`} aria-pressed={editing}
                            data-testid="canvas-edit-open" onMouseDown={(e) => e.stopPropagation()}>
                            <IconPencil size={14} />
                        </button>
                    )}
                    <button type="button" className="cvx-iconbtn cvx-focus" style={s.btn} onClick={onConfigure}
                        title="Settings" aria-label={`Configure ${meta.label} block`} onMouseDown={(e) => e.stopPropagation()}>
                        <IconGear size={14} />
                    </button>
                    <button type="button" className="cvx-iconbtn cvx-focus" style={s.btn} onClick={onDelete}
                        title="Delete" aria-label={`Delete ${meta.label} block`} onMouseDown={(e) => e.stopPropagation()}>
                        <IconTrash size={14} />
                    </button>
                </span>
                )}
            </div>
            <div style={s.body}>
                <Body block={block} running={running} statusSteps={statusSteps} readOnly={readOnly} hue={hue} Icon={meta.Icon} onRefresh={onRefresh} onConfigure={onConfigure} onCancelRun={onCancelRun} imageSrc={imageSrc} onConfigChange={onConfigChange} onArrangePivot={onArrangePivot} onChartSettings={onChartSettings}
                    editing={editing} savingEdit={savingEdit} onStartEdit={() => setEditing(true)} onCancelEdit={() => setEditing(false)} onCommitResult={commitResult} />
            </div>
        </div>
    );
}
